import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar, 
  Chip, 
  CircularProgress, 
  Alert,
  Grid,
} from '@mui/material';
import { 
  EmojiEvents as TrophyIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon, 
} from '@mui/icons-material';
import { useUser } from '../contexts/UserContext';
import { playerService, User, FantasyLeague, LeagueStanding } from '../services/playerService';
import { useTheme } from '@mui/material/styles';

interface LeagueStandingWithRank extends LeagueStanding {
  rank: number;
}

const StandingsPage: React.FC = () => {
  const theme = useTheme();
  const { currentUsername, isUserSwitching } = useUser();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [peroLeague, setPeroLeague] = useState<FantasyLeague | null>(null);
  const [standings, setStandings] = useState<LeagueStandingWithRank[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Memoize the loadStandingsData function to prevent recreation on every render
  const loadStandingsData = useCallback(async () => {
    if (!currentUsername) return;
    
    try {
      setLoading(true);
      setError(null);

      console.log(`🔄 StandingsPage: Loading data for user "${currentUsername}"...`);
      
      // Load user by username
      const user = await playerService.getUserByUsername(currentUsername);
      console.log('✅ StandingsPage: User loaded:', user);
      setCurrentUser(user);

      // Load user's fantasy leagues to find Pero league
      const userLeagues = await playerService.getFantasyLeaguesByUser(user.id);
      console.log('✅ StandingsPage: User leagues loaded:', userLeagues);
      
      // Find the Pero league
      const pero = userLeagues.find(league => league.name === 'Pero');
      
      if (!pero) {
        throw new Error('Pero league not found');
      }
      
      setPeroLeague(pero);
      console.log('✅ StandingsPage: Pero league found:', pero);

      // Load league standings
      const leagueStandings = await playerService.getLeagueStandings(pero.id);
      console.log('✅ StandingsPage: League standings loaded:', leagueStandings);
      
      // Add rank to each standing
      const standingsWithRank = leagueStandings.map((standing, index) => ({
        ...standing,
        rank: index + 1
      }));
      
      setStandings(standingsWithRank);

    } catch (err) {
      console.error('❌ StandingsPage: Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load standings data');
    } finally {
      setLoading(false);
    }
  }, [currentUsername]);

  // Load data when component mounts or when user changes
  useEffect(() => {
    if (currentUsername && !isUserSwitching) {
      loadStandingsData();
    }
  }, [currentUsername, isUserSwitching, loadStandingsData]);

  // Show loading state while user is switching
  if (isUserSwitching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Switching to {currentUsername}...
        </Typography>
      </Box>
    );
  }

  // Render loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading standings...
        </Typography>
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  // Get rank change indicator (placeholder for now)
  const getRankChange = (rank: number) => {
    // For now, just show the rank - could add trend indicators later
    return null;
  };

  // Get medal/rank icon
  const getRankIcon = (rank: number) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return null;
  };

  // Get team initials for avatar
  const getTeamInitials = (teamName: string) => {
    const words = teamName.split(' ');
    if (words.length >= 2) {
      return `${words[0][0]}${words[1][0]}`.toUpperCase();
    }
    return teamName.substring(0, 2).toUpperCase();
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Standings Header */}
      <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2, background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)` }}>
        <CardContent sx={{ p: 3, textAlign: 'center', color: 'white' }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <TrophyIcon sx={{ fontSize: 40 }} />
            League Standings
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            {peroLeague?.name || 'Pero'} League
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Current Season Ladder
          </Typography>
        </CardContent>
      </Card>

      {/* Standings Table */}
      <Card sx={{ borderRadius: 2, boxShadow: 2, overflow: 'hidden' }}>
        <TableContainer component={Paper} elevation={0}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: 80 }}>Rank</TableCell>
                <TableCell sx={{ fontWeight: 'bold', pl: 3 }}>Team</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Owner</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Avg FP/G</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total FP</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Players</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {standings.map((standing, index) => {
                const rankIcon = getRankIcon(standing.rank);
                const isTopThree = standing.rank <= 3;
                
                return (
                  <TableRow
                    key={standing.teamId}
                    sx={{
                      '&:hover': { bgcolor: 'action.hover' },
                      bgcolor: isTopThree ? 'action.selected' : 'inherit',
                      borderLeft: isTopThree ? `4px solid ${theme.palette.primary.main}` : 'none',
                    }}
                  >
                    {/* Rank */}
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        {rankIcon && (
                          <Typography component="span" sx={{ fontSize: '1.5rem' }}>
                            {rankIcon}
                          </Typography>
                        )}
                        <Typography
                          component="span"
                          sx={{
                            fontWeight: isTopThree ? 'bold' : 'normal',
                            color: isTopThree ? 'primary.main' : 'text.primary',
                            fontSize: isTopThree ? '1.2rem' : '1rem',
                          }}
                        >
                          {standing.rank}
          </Typography>
            </Box>
                    </TableCell>

                    {/* Team */}
                    <TableCell sx={{ pl: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: isTopThree ? 'primary.main' : 'secondary.main',
                            width: 48,
                            height: 48,
                            fontWeight: 'bold',
                            fontSize: '1rem',
                          }}
                        >
                          {getTeamInitials(standing.teamName)}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: isTopThree ? 'bold' : 'normal',
                              fontSize: isTopThree ? '1.1rem' : '1rem',
                            }}
                          >
                            {standing.teamName}
                          </Typography>
            </Box>
          </Box>
                    </TableCell>

                    {/* Owner */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {standing.ownerName}
          </Typography>
                    </TableCell>

                    {/* Average Fantasy Points per Game */}
                    <TableCell align="right">
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 'bold',
                          color: 'success.main',
                        }}
                      >
                        {standing.averageFantasyPointsPerGame.toFixed(1)}
                      </Typography>
                    </TableCell>

                    {/* Total Fantasy Points */}
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">
                        {Math.round(standing.totalFantasyPoints).toLocaleString()}
                      </Typography>
                    </TableCell>

                    {/* Player Count */}
                    <TableCell align="center">
                      <Chip
                        label={standing.playerCount}
                        size="small"
                        sx={{
                          bgcolor: 'action.hover',
                          fontWeight: 'medium',
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* League Summary Stats */}
      {standings.length > 0 && (
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ borderRadius: 2, boxShadow: 1, textAlign: 'center' }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Total Teams
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {standings.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ borderRadius: 2, boxShadow: 1, textAlign: 'center' }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  League Avg FP/G
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                  {(
                    standings.reduce((sum, s) => sum + s.averageFantasyPointsPerGame, 0) /
                    standings.length
                  ).toFixed(1)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ borderRadius: 2, boxShadow: 1, textAlign: 'center' }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Total Players
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                  {standings.reduce((sum, s) => sum + s.playerCount, 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default StandingsPage;
